import { useSyncExternalStore } from 'react';

/** Which cameras this device shows. Device state, kept in localStorage, never synced. */
export interface CameraConfig {
  // built-in camera; deviceId unset = rear; zoom unset = the lens's own view
  local: { active: boolean; deviceId?: string; zoom?: number };
  // key: bridge id; name and address are the last seen, so an absent bridge still lists
  bridges: Record<
    string,
    { active: boolean; hidden?: boolean; name: string; host: string; port: number }
  >;
  manual: { id: string; host: string; port: number }[]; // added by address
}

export const CAMERA_CONFIG_KEY = 'rosa.cameras.v1';

const isObject = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null && !Array.isArray(x);

const isAddress = (x: Record<string, unknown>) =>
  typeof x.host === 'string' && typeof x.port === 'number';

const isSavedBridge = (b: unknown) =>
  isObject(b) &&
  typeof b.name === 'string' &&
  isAddress(b) &&
  typeof b.active === 'boolean' &&
  (b.hidden === undefined || typeof b.hidden === 'boolean');

const isManual = (m: unknown) => isObject(m) && typeof m.id === 'string' && isAddress(m);

// Entries that fail validation are dropped one by one, so one bad entry keeps the rest.
function parse(raw: string | null): CameraConfig {
  try {
    const c: unknown = raw === null ? null : JSON.parse(raw);
    if (
      isObject(c) &&
      isObject(c.local) &&
      typeof c.local.active === 'boolean' &&
      isObject(c.bridges) &&
      Array.isArray(c.manual)
    ) {
      const { active, deviceId, zoom } = c.local;
      return {
        local: {
          active,
          ...(typeof deviceId === 'string' && { deviceId }),
          ...(typeof zoom === 'number' && Number.isFinite(zoom) && { zoom }),
        },
        bridges: Object.fromEntries(
          Object.entries(c.bridges).filter(([, b]) => isSavedBridge(b)),
        ) as CameraConfig['bridges'],
        manual: c.manual.filter(isManual) as CameraConfig['manual'],
      };
    }
  } catch {}
  return { local: { active: true }, bridges: {}, manual: [] };
}

// localStorage throws when site data is blocked; the config then reads as the default.
function read(storage: Storage): string | null {
  try {
    return storage.getItem(CAMERA_CONFIG_KEY);
  } catch {
    return null;
  }
}

export function loadCameraConfig(storage: Storage = localStorage): CameraConfig {
  return parse(read(storage));
}

const listeners = new Set<() => void>();
let lastRaw: string | null | undefined;
let last: CameraConfig;

// Re-parsed only when the stored text changes, so the snapshot keeps its identity.
function snapshot(): CameraConfig {
  const raw = read(localStorage);
  if (raw !== lastRaw) {
    lastRaw = raw;
    last = parse(raw);
  }
  return last;
}

function notify(): void {
  for (const l of [...listeners]) l();
}

function onStorage(e: StorageEvent): void {
  if (e.key === CAMERA_CONFIG_KEY || e.key === null) notify();
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
}

function updateCameraConfig(update: (c: CameraConfig) => CameraConfig): void {
  try {
    localStorage.setItem(CAMERA_CONFIG_KEY, JSON.stringify(update(snapshot())));
  } catch {}
  notify();
}

export function useCameraConfig(): [
  CameraConfig,
  (update: (c: CameraConfig) => CameraConfig) => void,
] {
  return [useSyncExternalStore(subscribe, snapshot), updateCameraConfig];
}
